import React, { type ChangeEvent, type KeyboardEvent, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import type {
  AgendaCalendarAppointment,
  AgendaCalendarResponse,
  AgendaCalendarSlot,
  AgendaLocation,
  Appointment,
  AppointmentSlot,
  Contact,
  Id,
  Opportunity,
  Professional,
} from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useDrawer } from "../context/DrawerContext";
import { formatDateTime } from "../lib/format";
import AgendaBooking from "../components/AgendaBooking";
import AgendaCalendarGrid from "../components/AgendaCalendarGrid";

type DateView = "day" | "week" | "month";

function startOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
}

function startOfWeek(value: Date) {
  const date = startOfDay(value);
  const weekday = date.getDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  date.setDate(date.getDate() - daysFromMonday);
  return date;
}

function endOfWeek(value: Date) {
  const date = startOfWeek(value);
  date.setDate(date.getDate() + 6);
  return endOfDay(date);
}

function startOfMonth(value: Date) {
  const date = startOfDay(value);
  date.setDate(1);
  return date;
}

function endOfMonth(value: Date) {
  const date = startOfMonth(value);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return endOfDay(date);
}

function localDateValue(value: Date) {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
}

function shiftAnchorDate(
  value: Date,
  view: DateView,
  direction: -1 | 1
) {
  const date = new Date(value);

  if (view === "day") {
    date.setDate(date.getDate() + direction);
  } else if (view === "week") {
    date.setDate(date.getDate() + direction * 7);
  } else {
    date.setDate(1);
    date.setMonth(date.getMonth() + direction);
  }

  return date;
}

function statusLabel(status?: string) {
  const labels: Record<string, string> = {
    booked: "Reservado",
    confirmed: "Confirmado",
    attended: "Atendido",
    completed: "Completado",
    cancelled: "Cancelado",
    canceled: "Cancelado",
    no_show: "Ausente",
    pending: "Pendiente",
  };

  return labels[status || ""] || status || "—";
}

function appointmentTypeLabel(type?: string) {
  const labels: Record<string, string> = {
    evaluation: "Evaluación",
    treatment: "Tratamiento",
    control: "Control",
    consultation: "Consulta",
  };

  return labels[type || ""] || type || "—";
}

export default function Agenda() {
  const { show } = useToast();
  const { hasPermission, permissionScope } = useAuth();
  const { open } = useDrawer();

  const canManage = hasPermission("agenda.manage");
  const scope = permissionScope("agenda.view");

  const [calendar, setCalendar] =
    useState<AgendaCalendarResponse | null>(null);
  const [appointments, setAppointments] =
    useState<AgendaCalendarAppointment[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);

  const [dateView, setDateView] = useState<DateView>("day");
  const [anchorDate, setAnchorDate] =
    useState(() => startOfDay(new Date()));
  const [displayMode, setDisplayMode] =
    useState<"calendar" | "list">("calendar");
  const [professionalId, setProfessionalId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<AgendaLocation[]>([]);
  const [status, setStatus] = useState("");

  const [previewAppointment, setPreviewAppointment] =
    useState<AgendaCalendarAppointment | null>(null);
  const [actionAppointment, setActionAppointment] =
    useState<Appointment | null>(null);
  const [actionMode, setActionMode] =
    useState<"cancel" | "reschedule" | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [availableSlots, setAvailableSlots] =
    useState<AppointmentSlot[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [newAppointmentOpen, setNewAppointmentOpen] =
    useState(false);
  const [bookingSeed, setBookingSeed] =
    useState<{
      professionalId: Id;
      slotId: Id;
      startsAt: string;
      locationId?: Id | null;
    } | null>(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientResults, setPatientResults] =
    useState<Contact[]>([]);
  const [patientSearchLoading, setPatientSearchLoading] =
    useState(false);
  const [selectedPatient, setSelectedPatient] =
    useState<Contact | null>(null);
  const [patientOpportunities, setPatientOpportunities] =
    useState<Opportunity[]>([]);
  const [selectedOpportunityId, setSelectedOpportunityId] =
    useState("");

  const selectedOpportunity = useMemo(
    () =>
      patientOpportunities.find(
        (opportunity) =>
          String(opportunity.id) === selectedOpportunityId
      ) || null,
    [patientOpportunities, selectedOpportunityId]
  );

  const dateParams = useMemo(() => {
    let from: Date;
    let to: Date;

    if (dateView === "day") {
      from = startOfDay(anchorDate);
      to = endOfDay(anchorDate);
    } else if (dateView === "week") {
      from = startOfWeek(anchorDate);
      to = endOfWeek(anchorDate);
    } else {
      from = startOfMonth(anchorDate);
      to = endOfMonth(anchorDate);
    }

    return {
      from: from.toISOString(),
      to: to.toISOString(),
    };
  }, [anchorDate, dateView]);

  const visibleRangeLabel = useMemo(() => {
    const from = new Date(dateParams.from);
    const to = new Date(dateParams.to);
    const formatter = new Intl.DateTimeFormat("es-AR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    if (dateView === "day") {
      return formatter.format(from);
    }

    return (
      formatter.format(from) +
      " — " +
      formatter.format(to)
    );
  }, [dateParams, dateView]);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const calendarData = await api.getAgendaCalendar({
        from: dateParams.from as string,
        to: dateParams.to as string,
        ...(professionalId
          ? { professional_id: professionalId as Id }
          : {}),
        ...(locationId
          ? { location_id: locationId as Id }
          : {}),
      });

      setCalendar(calendarData);
      if (!locationId) {
        setLocations(calendarData.locations);
      }
      setAppointments(
        status
          ? calendarData.appointments.filter(
              (appointment) => appointment.status === status
            )
          : calendarData.appointments
      );
      setProfessionals(calendarData.professionals.filter((p) => p.active));
    } catch (e) {
      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo cargar la agenda.", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [dateParams, locationId, professionalId, status, show]);

  useEffect(() => {
    load();
  }, [load]);

  function openPatient(appointment: Appointment) {
    if (appointment.contact_id) {
      open(appointment.contact_id);
    }
  }

  function resetNewAppointment() {
    setBookingSeed(null);
    setPatientSearch("");
    setPatientResults([]);
    setSelectedPatient(null);
    setPatientOpportunities([]);
    setSelectedOpportunityId("");
  }

  function closeNewAppointment() {
    setNewAppointmentOpen(false);
    resetNewAppointment();
  }

  async function searchPatients() {
    const term = patientSearch.trim();

    if (term.length < 2) {
      show(
        "Ingresá al menos 2 caracteres para buscar un paciente.",
        "error"
      );
      return;
    }

    setPatientSearchLoading(true);

    try {
      const rows = await api.searchContacts(term);
      setPatientResults(rows.slice(0, 30));

      if (rows.length === 0) {
        show("No encontramos pacientes con esa búsqueda.", "error");
      }
    } catch (e) {
      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo buscar pacientes.", "error");
      }
    } finally {
      setPatientSearchLoading(false);
    }
  }

  async function selectPatient(patient: Contact) {
    setSelectedPatient(patient);
    setPatientResults([]);
    setPatientOpportunities([]);
    setSelectedOpportunityId("");
    setPatientSearchLoading(true);

    try {
      const detail = await api.getContact(patient.id);

      const opportunities = detail.opportunities
        .filter((opportunity) =>
          !["lost", "closed", "cancelled", "canceled"].includes(
            opportunity.status || ""
          )
        )
        .sort((a, b) => {
          const aTime = a.created_at
            ? new Date(a.created_at).getTime()
            : 0;
          const bTime = b.created_at
            ? new Date(b.created_at).getTime()
            : 0;
          return bTime - aTime;
        });

      setPatientOpportunities(opportunities);

      if (opportunities.length === 1) {
        setSelectedOpportunityId(String(opportunities[0].id));
      }

      if (opportunities.length === 0) {
        show(
          "Este paciente no tiene oportunidades activas para asociar al turno.",
          "error"
        );
      }
    } catch (e) {
      setSelectedPatient(null);

      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo cargar la ficha del paciente.", "error");
      }
    } finally {
      setPatientSearchLoading(false);
    }
  }

  async function handleNewAppointmentBooked() {
    show("Turno creado desde Agenda.", "success");
    closeNewAppointment();
    await load();
  }

  function closeAction() {
    if (actionLoading) return;

    setActionAppointment(null);
    setActionMode(null);
    setCancelReason("");
    setAvailableSlots([]);
    setSelectedSlotId("");
  }

  function beginCancel(appointment: Appointment) {
    setActionAppointment(appointment);
    setActionMode("cancel");
    setCancelReason("");
    setAvailableSlots([]);
    setSelectedSlotId("");
  }

  async function beginReschedule(appointment: Appointment) {
    setActionAppointment(appointment);
    setActionMode("reschedule");
    setAvailableSlots([]);
    setSelectedSlotId("");
    setActionLoading(true);

    try {
      if (!appointment.professional_id) {
        throw new Error("El turno no tiene un profesional asignado.");
      }

      const from = new Date();
      const to = new Date();
      to.setDate(to.getDate() + 60);

      const rows = await api.getSlots(appointment.professional_id, {
        from: from.toISOString(),
        to: to.toISOString(),
        type: appointment.appointment_type || undefined,
      });

      const available = rows
        .filter((slot) => slot.status === "available")
        .sort(
          (a, b) =>
            new Date(a.starts_at).getTime() -
            new Date(b.starts_at).getTime()
        );

      setAvailableSlots(available);

      if (available.length === 0) {
        show(
          "No hay horarios disponibles para este profesional en los próximos 60 días.",
          "error"
        );
      }
    } catch (e) {
      setActionAppointment(null);
      setActionMode(null);

      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else if (e instanceof Error) {
        show(e.message, "error");
      } else {
        show("No se pudieron cargar los horarios disponibles.", "error");
      }
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmCancel() {
    if (!actionAppointment) return;

    setActionLoading(true);

    try {
      await api.cancelAppointment(actionAppointment.id, {
        reason: cancelReason.trim() || undefined,
      });

      show("Turno cancelado correctamente.", "success");
      closeAction();
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo cancelar el turno.", "error");
      }
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmReschedule() {
    if (!actionAppointment || !selectedSlotId) return;

    setActionLoading(true);

    try {
      await api.rescheduleAppointment(actionAppointment.id, {
        slot_id: selectedSlotId as Id,
      });

      show("Turno reprogramado correctamente.", "success");
      closeAction();
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) {
        show(e.message, "error");
      } else {
        show("No se pudo reprogramar el turno.", "error");
      }
    } finally {
      setActionLoading(false);
    }
  }

  const actionAllowed =
    actionAppointment &&
    !["cancelled", "canceled", "completed"].includes(
      actionAppointment.status || ""
    );

  return (
    <div className="agenda-page">
      <div className="kanban-header">
        <div>
          <h1>Agenda</h1>
          <p className="meta">
            Turnos, profesionales y actividad programada
            {scope ? ` · Alcance: ${scope}` : ""}
          </p>
        </div>

        {canManage && (
          <div className="agenda-header-actions">
            <button
              className="primary-btn agenda-new-btn"
              onClick={() => {
                resetNewAppointment();
                setNewAppointmentOpen(true);
              }}
            >
              + Nuevo turno
            </button>
          </div>
        )}
      </div>

      <div className="agenda-date-toolbar">
        <div className="agenda-date-navigation">
          <button
            className="secondary-btn"
            onClick={() =>
              setAnchorDate((current) =>
                shiftAnchorDate(current, dateView, -1)
              )
            }
            aria-label="Período anterior"
          >
            ‹
          </button>

          <button
            className="secondary-btn"
            onClick={() => setAnchorDate(startOfDay(new Date()))}
          >
            Hoy
          </button>

          <button
            className="secondary-btn"
            onClick={() =>
              setAnchorDate((current) =>
                shiftAnchorDate(current, dateView, 1)
              )
            }
            aria-label="Período siguiente"
          >
            ›
          </button>

          <input
            type="date"
            aria-label="Ir a una fecha"
            value={localDateValue(anchorDate)}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value) {
                setAnchorDate(
                  startOfDay(
                    new Date(event.target.value + "T12:00:00")
                  )
                );
              }
            }}
          />
        </div>

        <strong className="agenda-visible-range">
          {visibleRangeLabel}
        </strong>

        <div className="pipeline-tabs agenda-date-views">
          <button
            className={`tab ${dateView === "day" ? "active" : ""}`}
            onClick={() => setDateView("day")}
          >
            Día
          </button>

          <button
            className={`tab ${dateView === "week" ? "active" : ""}`}
            onClick={() => setDateView("week")}
          >
            Semana
          </button>

          <button
            className={`tab ${dateView === "month" ? "active" : ""}`}
            onClick={() => setDateView("month")}
          >
            Mes
          </button>
        </div>
      </div>

      <div className="agenda-filters">
        <label>
          <span>Profesional</span>
          <select
            value={professionalId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setProfessionalId(e.target.value)}
          >
            <option value="">Todos los profesionales</option>
            {professionals.map((professional) => (
              <option key={professional.id} value={professional.id}>
                {professional.full_name}
                {professional.specialty
                  ? ` · ${professional.specialty}`
                  : ""}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Sede</span>
          <select
            value={locationId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setLocationId(e.target.value)
            }
          >
            <option value="">Todas las sedes</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Estado</span>
          <select value={status} onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="booked">Reservado</option>
            <option value="confirmed">Confirmado</option>
            <option value="attended">Atendido</option>
            <option value="completed">Completado</option>
            <option value="cancelled">Cancelado</option>
            <option value="no_show">Ausente</option>
          </select>
        </label>

        <button className="secondary-btn" onClick={load} disabled={loading}>
          Actualizar
        </button>
      </div>

      {loading && (
        <div className="loading-strip">Cargando agenda...</div>
      )}

      <div className="agenda-summary">
        <strong>{appointments.length}</strong>
        <span>
          {appointments.length === 1
            ? " turno en esta vista"
            : " turnos en esta vista"}
        </span>
      </div>

      <div className="agenda-view-switch">
        <button
          className={`secondary-btn ${displayMode === "calendar" ? "active" : ""}`}
          onClick={() => setDisplayMode("calendar")}
        >
          Calendario
        </button>

        <button
          className={`secondary-btn ${displayMode === "list" ? "active" : ""}`}
          onClick={() => setDisplayMode("list")}
        >
          Listado
        </button>
      </div>

      {displayMode === "calendar" && calendar && (
        <AgendaCalendarGrid
          calendar={calendar}
          appointments={appointments}
          onAppointmentClick={(appointment) =>
            setPreviewAppointment(appointment)
          }
          onSlotClick={(slot: AgendaCalendarSlot) => {
            if (!canManage) return;
            resetNewAppointment();
            setBookingSeed({
              professionalId: slot.professional_id,
              slotId: slot.id,
              startsAt: slot.starts_at,
              locationId: slot.location_id ?? null,
            });
            setNewAppointmentOpen(true);
          }}
        />
      )}

      {displayMode === "list" && (
        <div className="agenda-table-wrap">
        <table className="task-table agenda-table">
          <thead>
            <tr>
              <th>Fecha y hora</th>
              <th>Paciente</th>
              <th>Profesional</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Confirmación</th>
              {canManage && <th>Acciones</th>}
            </tr>
          </thead>

          <tbody>
            {appointments.map((appointment) => (
              <tr key={appointment.id}>
                <td>{formatDateTime(appointment.starts_at)}</td>

                <td>
                  <button
                    className="link-btn"
                    onClick={() => openPatient(appointment)}
                  >
                    {appointment.contact_name || "Ver paciente"}
                  </button>

                  {appointment.contact_phone && (
                    <div className="meta">
                      {appointment.contact_phone}
                    </div>
                  )}
                </td>

                <td>
                  {appointment.professional_name || "Sin asignar"}
                </td>

                <td>
                  {appointmentTypeLabel(
                    appointment.appointment_type
                  )}
                </td>

                <td>
                  <span
                    className={`agenda-status agenda-status-${appointment.status || "unknown"}`}
                  >
                    {statusLabel(appointment.status)}
                  </span>
                </td>

                <td>
                  {appointment.confirmation_status || "—"}
                </td>

                {canManage && (
                  <td>
                    {!["cancelled", "canceled", "completed"].includes(
                      appointment.status || ""
                    ) ? (
                      <div className="agenda-row-actions">
                        <button
                          className="secondary-btn agenda-action-btn"
                          onClick={() => beginReschedule(appointment)}
                          disabled={actionLoading}
                        >
                          Reprogramar
                        </button>

                        <button
                          className="secondary-btn agenda-action-btn agenda-cancel-btn"
                          onClick={() => beginCancel(appointment)}
                          disabled={actionLoading}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}

            {!loading && appointments.length === 0 && (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="meta">
                  No hay turnos para los filtros seleccionados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {previewAppointment && (
        <div
          className="agenda-action-backdrop"
          onClick={() => setPreviewAppointment(null)}
        >
          <div
            className="agenda-action-dialog agenda-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-preview-title"
            onClick={(e: MouseEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
          >
            <div className="agenda-action-dialog-header">
              <div>
                <h2 id="agenda-preview-title">
                  {previewAppointment.contact_name || "Turno"}
                </h2>
                <p className="meta">
                  Vista operativa del turno
                </p>
              </div>

              <button
                className="agenda-dialog-close"
                onClick={() => setPreviewAppointment(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="agenda-action-content">
              <div className="agenda-preview-grid">
                <div>
                  <span>Inicio</span>
                  <strong>
                    {formatDateTime(previewAppointment.starts_at)}
                  </strong>
                </div>

                <div>
                  <span>Finalización</span>
                  <strong>
                    {formatDateTime(previewAppointment.ends_at)}
                  </strong>
                </div>

                <div>
                  <span>Profesional</span>
                  <strong>
                    {previewAppointment.professional_name ||
                      "Sin asignar"}
                  </strong>
                </div>

                <div>
                  <span>Sede</span>
                  <strong>
                    {previewAppointment.location_name ||
                      "Sin sede asignada"}
                  </strong>
                </div>

                <div>
                  <span>Tipo</span>
                  <strong>
                    {appointmentTypeLabel(
                      previewAppointment.appointment_type
                    )}
                  </strong>
                </div>

                <div>
                  <span>Estado</span>
                  <strong>
                    {statusLabel(previewAppointment.status)}
                  </strong>
                </div>
              </div>

              <div className="agenda-preview-resources">
                <span>Recursos asignados</span>
                <strong>
                  {previewAppointment.resources.length > 0
                    ? previewAppointment.resources
                        .map((resource) => resource.name)
                        .join(" · ")
                    : "Sin recursos asignados"}
                </strong>
              </div>
            </div>

            <div className="agenda-dialog-actions">
              <button
                className="secondary-btn"
                onClick={() => {
                  openPatient(previewAppointment);
                  setPreviewAppointment(null);
                }}
              >
                Ver paciente
              </button>

              {canManage &&
                !["cancelled", "canceled", "completed"].includes(
                  previewAppointment.status || ""
                ) && (
                  <>
                    <button
                      className="secondary-btn"
                      onClick={() => {
                        const appointment = previewAppointment;
                        setPreviewAppointment(null);
                        beginReschedule(appointment);
                      }}
                    >
                      Reprogramar
                    </button>

                    <button
                      className="agenda-danger-btn"
                      onClick={() => {
                        const appointment = previewAppointment;
                        setPreviewAppointment(null);
                        beginCancel(appointment);
                      }}
                    >
                      Cancelar
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      {newAppointmentOpen && (
        <div
          className="agenda-action-backdrop"
          onClick={closeNewAppointment}
        >
          <div
            className="agenda-action-dialog agenda-new-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-new-title"
            onClick={(e: MouseEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
          >
            <div className="agenda-action-dialog-header">
              <div>
                <h2 id="agenda-new-title">Nuevo turno</h2>
                <p className="meta">
                  Buscá el paciente, elegí la oportunidad y
                  seleccioná un horario disponible.
                </p>
              </div>

              <button
                className="agenda-dialog-close"
                onClick={closeNewAppointment}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="agenda-action-content">
              {!selectedPatient && (
                <>
                  <label className="agenda-action-field">
                    <span>Buscar paciente</span>

                    <div className="agenda-patient-search">
                      <input
                        value={patientSearch}
                        onChange={(
                          e: ChangeEvent<HTMLInputElement>
                        ) => setPatientSearch(e.target.value)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void searchPatients();
                          }
                        }}
                        placeholder="Nombre, teléfono o email"
                        autoFocus
                      />

                      <button
                        className="secondary-btn"
                        onClick={searchPatients}
                        disabled={patientSearchLoading}
                      >
                        {patientSearchLoading
                          ? "Buscando..."
                          : "Buscar"}
                      </button>
                    </div>
                  </label>

                  {patientResults.length > 0 && (
                    <div className="agenda-patient-results">
                      {patientResults.map((patient) => (
                        <button
                          key={patient.id}
                          className="agenda-patient-result"
                          onClick={() => selectPatient(patient)}
                        >
                          <strong>{patient.full_name}</strong>

                          <span className="meta">
                            {patient.phone || "Sin teléfono"}
                            {patient.email
                              ? ` · ${patient.email}`
                              : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {selectedPatient && (
                <>
                  <div className="agenda-selected-patient">
                    <div>
                      <span className="meta">Paciente</span>
                      <strong>{selectedPatient.full_name}</strong>

                      <span className="meta">
                        {selectedPatient.phone || "Sin teléfono"}
                      </span>
                    </div>

                    <button
                      className="link-btn"
                      onClick={() => {
                        setSelectedPatient(null);
                        setPatientOpportunities([]);
                        setSelectedOpportunityId("");
                      }}
                    >
                      Cambiar paciente
                    </button>
                  </div>

                  {patientSearchLoading ? (
                    <div className="loading-strip">
                      Cargando oportunidades...
                    </div>
                  ) : (
                    <>
                      <label className="agenda-action-field">
                        <span>Oportunidad</span>

                        <select
                          value={selectedOpportunityId}
                          onChange={(
                            e: ChangeEvent<HTMLSelectElement>
                          ) =>
                            setSelectedOpportunityId(
                              e.target.value
                            )
                          }
                        >
                          <option value="">
                            Elegí una oportunidad...
                          </option>

                          {patientOpportunities.map(
                            (opportunity) => (
                              <option
                                key={opportunity.id}
                                value={opportunity.id}
                              >
                                {opportunity.service_name ||
                                  opportunity.service_code ||
                                  "Oportunidad"}
                                {opportunity.stage_name
                                  ? ` · ${opportunity.stage_name}`
                                  : ""}
                              </option>
                            )
                          )}
                        </select>
                      </label>

                      {patientOpportunities.length === 0 && (
                        <div className="agenda-empty-opportunity">
                          <strong>
                            Sin oportunidades activas
                          </strong>
                          <p className="meta">
                            Para crear un turno necesitamos una
                            oportunidad asociada al paciente.
                          </p>
                        </div>
                      )}

                      {selectedOpportunity?.service_id && (
                        <div className="agenda-new-booking">
                          <AgendaBooking
                            initialProfessionalId={
                              bookingSeed?.professionalId
                            }
                            initialSlotId={bookingSeed?.slotId}
                            initialDate={bookingSeed?.startsAt}
                            initialLocationId={
                              bookingSeed?.locationId ??
                              (locationId
                                ? (locationId as Id)
                                : null)
                            }
                            locations={locations}
                            opportunityId={
                              selectedOpportunity.id
                            }
                            serviceId={
                              selectedOpportunity.service_id
                            }
                            onBooked={
                              handleNewAppointmentBooked
                            }
                          />
                        </div>
                      )}

                        {selectedOpportunityId &&
                          !selectedOpportunity?.service_id && (
                            <p className="meta">
                              La oportunidad seleccionada no tiene un
                              tratamiento asignado.
                            </p>
                          )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {actionAppointment && actionMode && (
        <div className="agenda-action-backdrop" onClick={closeAction}>
          <div
            className="agenda-action-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-action-title"
            onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            <div className="agenda-action-dialog-header">
              <div>
                <h2 id="agenda-action-title">
                  {actionMode === "cancel"
                    ? "Cancelar turno"
                    : "Reprogramar turno"}
                </h2>

                <p className="meta">
                  {actionAppointment.contact_name || "Paciente"}
                  {" · "}
                  {formatDateTime(actionAppointment.starts_at)}
                </p>
              </div>

              <button
                className="agenda-dialog-close"
                onClick={closeAction}
                disabled={actionLoading}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            {actionMode === "cancel" && (
              <div className="agenda-action-content">
                <p>
                  El turno quedará cancelado y sus recordatorios pendientes
                  serán anulados.
                </p>

                <label className="agenda-action-field">
                  <span>Motivo de cancelación</span>
                  <textarea
                    value={cancelReason}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setCancelReason(e.target.value)}
                    placeholder="Opcional"
                    rows={3}
                    disabled={actionLoading}
                  />
                </label>

                <div className="agenda-dialog-actions">
                  <button
                    className="secondary-btn"
                    onClick={closeAction}
                    disabled={actionLoading}
                  >
                    Volver
                  </button>

                  <button
                    className="agenda-danger-btn"
                    onClick={confirmCancel}
                    disabled={actionLoading || !actionAllowed}
                  >
                    {actionLoading ? "Cancelando..." : "Confirmar cancelación"}
                  </button>
                </div>
              </div>
            )}

            {actionMode === "reschedule" && (
              <div className="agenda-action-content">
                <p>
                  Seleccioná un nuevo horario disponible para{" "}
                  <strong>
                    {actionAppointment.professional_name ||
                      "el profesional"}
                  </strong>.
                </p>

                {actionLoading && availableSlots.length === 0 ? (
                  <div className="loading-strip">
                    Buscando horarios disponibles...
                  </div>
                ) : (
                  <label className="agenda-action-field">
                    <span>Nuevo horario</span>

                    <select
                      value={selectedSlotId}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setSelectedSlotId(e.target.value)
                      }
                      disabled={
                        actionLoading || availableSlots.length === 0
                      }
                    >
                      <option value="">
                        {availableSlots.length
                          ? "Seleccionar fecha y hora"
                          : "Sin horarios disponibles"}
                      </option>

                      {availableSlots.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {formatDateTime(slot.starts_at)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <div className="agenda-dialog-actions">
                  <button
                    className="secondary-btn"
                    onClick={closeAction}
                    disabled={actionLoading}
                  >
                    Volver
                  </button>

                  <button
                    className="primary-btn"
                    onClick={confirmReschedule}
                    disabled={
                      actionLoading ||
                      !selectedSlotId ||
                      !actionAllowed
                    }
                  >
                    {actionLoading
                      ? "Reprogramando..."
                      : "Confirmar nuevo horario"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
